import { useCallback, useEffect, useState } from "react"

import cloneDeep from "lodash.clonedeep"
import get from "lodash.get"
import set from "lodash.set"

import { ArrayItemCopyDefinition } from "./ArrayItemCopyAndReplaceModal"
import { ParentSiblingArrayCopyDefinition } from "./ParentSiblingArrayCopyModal"
import { cloneFormData } from "./formUtils"

export interface ExtendedRJSFormContext {
  triggerSiblingParentArrayCopy: (definition: {
    fieldDepth: string[]
    fieldTitleProperty?: string
    uncopyableProperties?: string[]
  }) => void
  triggerArrayItemCopy: (definition: {
    currentItemIndex: number
    fieldDepth: string[]
    fieldTitleProperty?: string
    uncopyableProperties?: string[]
  }) => void
  pushToFormDataArrayUpdateQueue: (fieldDepth: string[], items: unknown[]) => void
}

interface ExtendedRJSFormHook<T> {
  formData: T
  setFormData: (formData: T) => void
  formContext: ExtendedRJSFormContext
  parentSiblingArrayCopyDefinition?: ParentSiblingArrayCopyDefinition
  handleParentSiblingArrayCopyDismiss: () => void
  handleParentSiblingArrayCopy: (targetDepth: string[], items: unknown[]) => void
  arrayItemCopyDefinition?: ArrayItemCopyDefinition
  handleArrayItemCopyDismiss: () => void
  handleArrayItemCopy: (targetDepth: string[], item: unknown) => void
}

export default function useExtendedRJSForm<T = unknown>(initialFormData: T): ExtendedRJSFormHook<T> {
  const [formData, setFormData] = useState<T>(initialFormData)
  const [formDataArrayPushQueue, setFormDataArrayPushQueue] = useState<{
    fieldDepth: string[]
    items: unknown[]
  } | null>(null)
  const [formDataUpdateQueue, setFormDataUpdateQueue] = useState<{
    fieldDepth: string[]
    value: unknown
  } | null>(null)

  useEffect(() => {
    setFormData(initialFormData)
  }, [initialFormData])

  useEffect(() => {
    if (formDataArrayPushQueue) {
      const { fieldDepth, items } = formDataArrayPushQueue

      const formDataCopy = cloneDeep(formData)
      const target = get(formDataCopy, fieldDepth)

      cloneFormData(items).forEach((item) => {
        target.push(item)
      })
      setFormData(formDataCopy)
      setFormDataArrayPushQueue(null)
    }
  }, [formDataArrayPushQueue])

  useEffect(() => {
    if (formDataUpdateQueue) {
      const { fieldDepth, value } = formDataUpdateQueue

      setFormData((currentFormData) => {
        const formDataCopy = cloneDeep(currentFormData)

        set(formDataCopy as object, fieldDepth, cloneFormData(value))

        return formDataCopy
      })
      setFormDataUpdateQueue(null)
    }
  }, [formDataUpdateQueue])

  const pushToFormDataArrayUpdateQueue = (fieldDepth: string[], items: unknown[]) => {
    setFormDataArrayPushQueue({ fieldDepth, items })
  }

  // PARENT SIBLING ARRAY COPY
  const [parentSiblingArrayCopyDefinition, setParentSiblingArrayCopyDefinition] = useState<
    ParentSiblingArrayCopyDefinition | undefined
  >(undefined)

  const triggerSiblingParentArrayCopy = useCallback<ExtendedRJSFormContext["triggerSiblingParentArrayCopy"]>(
    ({ fieldDepth, uncopyableProperties, fieldTitleProperty }) => {
      setParentSiblingArrayCopyDefinition({
        fieldDepth,
        parentTitleProperty: fieldTitleProperty,
        uncopyableProperties
      })
    },
    []
  )

  const handleParentSiblingArrayCopy = useCallback<ExtendedRJSFormHook<T>["handleParentSiblingArrayCopy"]>(
    (targetDepth, items) => {
      const formDataCopy = cloneDeep(formData)
      const target = get(formDataCopy, targetDepth)

      items.forEach((item) => {
        target.push(item)
      })
      setFormData(formDataCopy)

      setParentSiblingArrayCopyDefinition(undefined)
    },
    [formData]
  )

  const handleParentSiblingArrayCopyDismiss = useCallback(() => {
    setParentSiblingArrayCopyDefinition(undefined)
  }, [])

  // ARRAY ITEM COPY
  const [arrayItemCopyDefinition, setArrayItemCopyDefinition] = useState<ArrayItemCopyDefinition | undefined>(undefined)
  const triggerArrayItemCopy = useCallback<ExtendedRJSFormContext["triggerArrayItemCopy"]>(
    ({ currentItemIndex, fieldDepth, uncopyableProperties, fieldTitleProperty }) => {
      setArrayItemCopyDefinition({
        currentItemIndex,
        fieldDepth,
        itemTitleProperty: fieldTitleProperty,
        uncopyableProperties
      })
    },
    []
  )

  const handleArrayItemCopy = useCallback<ExtendedRJSFormHook<T>["handleArrayItemCopy"]>(
    (targetDepth, item) => {
      const formDataCopy = cloneDeep(formData)

      set(formDataCopy as object, targetDepth, cloneFormData(item))
      setFormData(formDataCopy)
      setArrayItemCopyDefinition(undefined)
    },
    [formData]
  )

  const handleArrayItemCopyDismiss = useCallback(() => {
    setArrayItemCopyDefinition(undefined)
  }, [])

  return {
    formData,
    setFormData,
    parentSiblingArrayCopyDefinition,
    handleParentSiblingArrayCopy,
    handleParentSiblingArrayCopyDismiss,
    arrayItemCopyDefinition,
    handleArrayItemCopy,
    handleArrayItemCopyDismiss,
    formContext: {
      triggerSiblingParentArrayCopy,
      triggerArrayItemCopy,
      pushToFormDataArrayUpdateQueue
    }
  }
}
